import { Link, useParams } from "react-router-dom";
import { formatWhen, lifecycleLabel } from "@/lib/format";
import { OPERATION_TERMINAL } from "@/lib/types";
import { useOperationPoll } from "@/lib/useOperationPoll";
import { Button } from "@/components/Button";
import { ButtonLink } from "@/components/ButtonLink";
import { StatePanel } from "@/components/StatePanel";
import { StatusRail } from "@/components/StatusRail";
import { useLocale } from "@/locale/LocaleContext";
import shell from "@/components/shell.module.css";
import ui from "@/components/ui.module.css";

function resourceLink(
  kind: string,
  resourceId: string,
  labels: { lease: string; task: string },
): { to: string; label: string } | null {
  if (/lease/i.test(kind)) return { to: `/app/leases/${resourceId}`, label: labels.lease };
  if (/task/i.test(kind)) return { to: `/app/tasks/${resourceId}`, label: labels.task };
  return null;
}

export function OperationPage() {
  const { operationId = "" } = useParams();
  const { m, locale } = useLocale();
  const { operation, error, loading } = useOperationPoll(operationId);

  if (loading && !operation) {
    return <StatePanel kind="pending" title={m.common.loading} />;
  }

  if (error && !operation) {
    return <StatePanel kind="error" title={m.states.error} message={error} />;
  }

  if (!operation) {
    return <StatePanel kind="empty" title={m.states.empty} />;
  }

  const terminal = OPERATION_TERMINAL.has(operation.state);
  const link =
    operation.resource_id != null && operation.resource_id !== ""
      ? resourceLink(operation.kind, operation.resource_id, {
          lease: m.operations.openLease,
          task: m.operations.openTask,
        })
      : null;

  return (
    <div>
      <p className={ui.meta}>
        <Link to="/app">← {m.operations.back}</Link>
      </p>
      <h1 className={shell.pageTitle}>{m.operations.title}</h1>
      <p className={shell.pageLead}>{m.operations.lead}</p>

      <div className={ui.row} style={{ cursor: "default" }}>
        <StatusRail state={operation.state} pulse={!terminal} />
        <div>
          <div>
            <strong>{lifecycleLabel(m, operation.state)}</strong> · {lifecycleLabel(m, operation.kind)}
          </div>
          <div className={`mono ${ui.meta}`}>
            {m.operations.opLabel} {operation.id}
          </div>
          <div className={`mono ${ui.meta}`}>
            {m.operations.objectLabel} {operation.resource_id ?? m.operations.waitingObject}
          </div>
          <div className={ui.meta}>
            {formatWhen(operation.created_at, locale)} · {formatWhen(operation.updated_at, locale)}
          </div>
        </div>
      </div>

      {!terminal ? (
        <StatePanel kind="pending" title={m.operations.runningTitle} message={m.operations.runningBody} />
      ) : null}

      {operation.state === "failed" || operation.state === "canceled" ? (
        <StatePanel
          kind="error"
          title={operation.state === "canceled" ? m.operations.canceledTitle : m.operations.failedTitle}
          message={operation.error?.message ?? m.operations.failedBody}
          detail={operation.error?.request_id}
        />
      ) : null}

      {operation.state === "succeeded" ? (
        <StatePanel
          kind="empty"
          title={m.operations.succeededTitle}
          message={m.operations.succeededBody}
        />
      ) : null}

      {error ? (
        <p className={ui.fieldError}>
          {m.operations.pollError}: {error}
        </p>
      ) : null}

      <div className={ui.actions} style={{ justifyContent: "flex-start", marginTop: "1rem" }}>
        {link ? (
          <ButtonLink to={link.to} variant="primary">
            {link.label}
          </ButtonLink>
        ) : (
          <Button disabled>
            {operation.resource_id ? m.operations.unknownResource : m.operations.noResource}
          </Button>
        )}
        <ButtonLink to="/app/machines" variant="secondary">
          {m.operations.toMachines}
        </ButtonLink>
      </div>
    </div>
  );
}
